// ADR-132: ingestion configuration — job position mappings (radar-api /api/ingestion/…).
import {
  authenticatedApi,
  ApiError,
  API_BASE_URL
} from './apiUtils';

const BASE = `${API_BASE_URL}/ingestion/job-position-mappings`;

// Keep the server's message and payload (duplicate pattern, unresolved import
// rows, …): on this page they are the actionable part of the error.
const rethrow = (error, fallback) => {
  throw new ApiError(error?.message || fallback, error?.status || 500, error?.data || null);
};

export const ingestionConfigurationApi = {
  /** The mapping set for a provider: { provider, version, count, updatedAt, updatedBy, mappings[] } */
  getJobPositionMappings: async (provider) => {
    try {
      return await authenticatedApi.get(`${BASE}?provider=${encodeURIComponent(provider)}`);
    } catch (error) {
      return rethrow(error, 'Failed to load job position mappings');
    }
  },

  /** @returns {Promise<{ mapping, version, warnings[] }>} */
  createJobPositionMapping: async ({ provider, sourcePattern, outcome, positionId, notes }) => {
    try {
      return await authenticatedApi.post(BASE, {
        provider,
        sourcePattern,
        outcome,
        positionId: positionId || null,
        notes: notes || null,
      });
    } catch (error) {
      return rethrow(error, 'Failed to add job position mapping');
    }
  },

  /** @returns {Promise<{ mapping, version, warnings[] }>} */
  updateJobPositionMapping: async (id, { sourcePattern, outcome, positionId, notes }) => {
    try {
      return await authenticatedApi.put(`${BASE}/${id}`, {
        sourcePattern,
        outcome,
        positionId: positionId || null,
        notes: notes || null,
      });
    } catch (error) {
      return rethrow(error, 'Failed to update job position mapping');
    }
  },

  deleteJobPositionMapping: async (id) => {
    try {
      return await authenticatedApi.delete(`${BASE}/${id}`);
    } catch (error) {
      return rethrow(error, 'Failed to remove job position mapping');
    }
  },

  /**
   * Bulk import. dryRun=true (default) only plans and returns the report;
   * dryRun=false applies it atomically and returns the new version.
   */
  importJobPositionMappings: async ({ provider, items, mode, dryRun = true }) => {
    try {
      return await authenticatedApi.post(`${BASE}/import?dryRun=${dryRun ? 'true' : 'false'}`, { provider, items, mode });
    } catch (error) {
      return rethrow(error, 'Failed to import job position mappings');
    }
  },

  exportJobPositionMappings: async (provider) => {
    try {
      return await authenticatedApi.get(`${BASE}/export?provider=${encodeURIComponent(provider)}`);
    } catch (error) {
      return rethrow(error, 'Failed to export job position mappings');
    }
  },

  /**
   * ADR-133 D11: titles the provider's last recorded employee run could not map.
   * @returns {Promise<{ runId, runStartedAt, mappingSetVersionUsed, currentMappingSetVersion, titles: [{ title, coveredByExactMapping }] }>}
   */
  getUnmappedTitles: async (provider) => {
    try {
      return await authenticatedApi.get(`${BASE}/unmapped?provider=${encodeURIComponent(provider)}`);
    } catch (error) {
      return rethrow(error, 'Failed to load unmapped titles');
    }
  },

  /** @returns {Promise<{ items[], pageInfo: { hasNextPage, endCursor } }>} */
  getJobPositionMappingHistory: async (provider, { limit = 50, cursor } = {}) => {
    try {
      const params = new URLSearchParams({ provider, limit: String(limit) });
      if (cursor) {
        params.set('cursor', cursor);
      }
      return await authenticatedApi.get(`${BASE}/history?${params.toString()}`);
    } catch (error) {
      return rethrow(error, 'Failed to load mapping history');
    }
  },
};
